import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useAuth } from '../../contexts/AuthContext';

type Device = {
  id: string;
  name: string;
};

type RuleCondition = {
  cameraId: string;
  comparison: string;
  threshold: number;
  classes?: number[];
};

type RuleConditions = {
  operator: 'AND' | 'OR';
  conditions?: RuleCondition[];
  groups?: RuleConditions[];
  timeDelaySeconds?: number;
};

type DeviceRule = {
  id: string;
  parentDeviceId: string;
  ruleName: string;
  conditionsJson: string;
  actionData?: string;
  isEnabled: boolean;
  description?: string;
  evaluationWindowMinutes: number;
  cooldownSeconds: number;
  sendSmsOnTrigger: boolean;
  disableAfterTrigger: boolean;
  dateCreated: string;
  lastTriggeredAt?: string;
};

type Props = { deviceId: string };

type RelayAction = {
  type: 'relay';
  relay_number: number;
  action: 'on' | 'off';
  duration: number;
  target_device_id?: string;
};

const parseNumber = (value: any, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeConditions = (raw: any): RuleConditions => {
  const operator = raw?.operator || raw?.Operator || 'AND';
  const rawConditions = raw?.conditions || raw?.Conditions || [];
  const rawGroups = raw?.groups || raw?.Groups || [];
  const timeDelaySeconds = raw?.timeDelaySeconds ?? raw?.TimeDelaySeconds ?? raw?.time_delay_seconds;

  return {
    operator: operator === 'OR' ? 'OR' : 'AND',
    timeDelaySeconds: typeof timeDelaySeconds === 'number' ? timeDelaySeconds : undefined,
    conditions: Array.isArray(rawConditions)
      ? rawConditions.map((condition: any) => ({
          cameraId: condition?.cameraId || condition?.CameraId || condition?.camera_id || '',
          comparison: condition?.comparison || condition?.Comparison || '>=',
          threshold: parseNumber(condition?.threshold ?? condition?.Threshold, 1),
          classes: Array.isArray(condition?.classes || condition?.Classes)
            ? (condition.classes || condition.Classes)
                .map((c: any) => parseNumber(c, NaN))
                .filter((c: number) => Number.isFinite(c))
            : [],
        }))
      : [],
    groups: Array.isArray(rawGroups) ? rawGroups.map((group: any) => normalizeConditions(group)) : [],
  };
};

// Recursive condition group builder component
type ConditionGroupBuilderProps = {
  conditions: RuleConditions;
  onChange: (newConditions: RuleConditions) => void;
  childDevices: Device[];
  depth: number;
};

const ConditionGroupBuilder: React.FC<ConditionGroupBuilderProps> = ({
  conditions,
  onChange,
  childDevices,
  depth
}) => {
  const addCondition = () => {
    const newConditions = { ...conditions };
    if (!newConditions.conditions) newConditions.conditions = [];
    newConditions.conditions.push({ cameraId: '', comparison: '>=', threshold: 1, classes: [] });
    onChange(newConditions);
  };

  const removeCondition = (index: number) => {
    const newConditions = { ...conditions };
    newConditions.conditions = newConditions.conditions?.filter((_, i) => i !== index);
    onChange(newConditions);
  };

  const updateCondition = (index: number, field: keyof RuleCondition, value: any) => {
    const newConditions = { ...conditions };
    if (newConditions.conditions) {
      newConditions.conditions[index] = { ...newConditions.conditions[index], [field]: value };
    }
    onChange(newConditions);
  };

  const addGroup = () => {
    const newConditions = { ...conditions };
    if (!newConditions.groups) newConditions.groups = [];
    newConditions.groups.push({
      operator: 'AND',
      conditions: [{ cameraId: '', comparison: '>=', threshold: 1, classes: [] }],
      groups: []
    });
    onChange(newConditions);
  };

  const removeGroup = (index: number) => {
    const newConditions = { ...conditions };
    newConditions.groups = newConditions.groups?.filter((_, i) => i !== index);
    onChange(newConditions);
  };

  const updateGroup = (index: number, newGroup: RuleConditions) => {
    const newConditions = { ...conditions };
    if (newConditions.groups) {
      newConditions.groups[index] = newGroup;
    }
    onChange(newConditions);
  };

  const updateOperator = (newOperator: 'AND' | 'OR') => {
    onChange({ ...conditions, operator: newOperator });
  };

  const updateTimeDelay = (seconds: number) => {
    onChange({ ...conditions, timeDelaySeconds: seconds || undefined });
  };

  const indentLevel = depth * 20;
  const borderColor = depth === 0 ? 'primary.main' : depth === 1 ? 'secondary.main' : 'divider';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        ml: `${indentLevel}px`,
        borderWidth: 2,
        borderColor: borderColor,
        bgcolor: depth === 0 ? 'background.paper' : 'background.default'
      }}
    >
      <Stack spacing={2}>
        {/* Group Header */}
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Operator</InputLabel>
            <Select
              value={conditions.operator}
              onChange={(e) => updateOperator(e.target.value as 'AND' | 'OR')}
              label="Operator"
              size="small"
            >
              <MenuItem value="AND">AND (all must match)</MenuItem>
              <MenuItem value="OR">OR (any can match)</MenuItem>
            </Select>
          </FormControl>

          {depth > 0 && (
            <TextField
              label="Time Delay (seconds)"
              type="number"
              value={conditions.timeDelaySeconds || 0}
              onChange={(e) => updateTimeDelay(parseNumber(e.target.value, 0))}
              size="small"
              sx={{ width: 180 }}
              helperText="0 = no delay"
            />
          )}

          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Depth: {depth}
          </Typography>
        </Stack>

        {/* Individual Conditions */}
        {conditions.conditions?.map((condition, index) => (
          <Paper key={index} elevation={0} sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" fontWeight="bold">Condition {index + 1}</Typography>
                <IconButton size="small" onClick={() => removeCondition(index)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>

              <FormControl fullWidth size="small">
                <InputLabel>Camera</InputLabel>
                <Select
                  value={condition.cameraId}
                  onChange={(e) => updateCondition(index, 'cameraId', e.target.value)}
                  label="Camera"
                >
                  {childDevices.map((device) => (
                    <MenuItem key={device.id} value={device.id}>
                      {device.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <FormControl sx={{ minWidth: 100 }} size="small">
                  <InputLabel>Comparison</InputLabel>
                  <Select
                    value={condition.comparison}
                    onChange={(e) => updateCondition(index, 'comparison', e.target.value)}
                    label="Comparison"
                  >
                    <MenuItem value=">=">&gt;=</MenuItem>
                    <MenuItem value="<=">&lt;=</MenuItem>
                    <MenuItem value="==">=</MenuItem>
                    <MenuItem value=">">&gt;</MenuItem>
                    <MenuItem value="<">&lt;</MenuItem>
                    <MenuItem value="!=">!=</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Threshold"
                  type="number"
                  value={condition.threshold}
                  onChange={(e) => updateCondition(index, 'threshold', parseNumber(e.target.value, 0))}
                  size="small"
                  sx={{ flex: 1 }}
                />
              </Stack>

              <TextField
                label="Classes (comma-separated, e.g., 0,15)"
                value={condition.classes?.join(',') || ''}
                onChange={(e) => {
                  const classes = e.target.value
                    ? e.target.value.split(',').map(c => parseNumber(c.trim(), NaN)).filter(c => Number.isFinite(c))
                    : [];
                  updateCondition(index, 'classes', classes);
                }}
                size="small"
                fullWidth
                helperText="Leave empty to monitor all classes"
              />
            </Stack>
          </Paper>
        ))}

        <Button startIcon={<AddIcon />} onClick={addCondition} variant="outlined" size="small">
          Add Condition
        </Button>

        {/* Nested Groups */}
        {conditions.groups?.map((group, index) => (
          <Box key={index} sx={{ position: 'relative' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" fontWeight="bold">Group {index + 1}</Typography>
              <IconButton size="small" onClick={() => removeGroup(index)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <ConditionGroupBuilder
              conditions={group}
              onChange={(newGroup) => updateGroup(index, newGroup)}
              childDevices={childDevices}
              depth={depth + 1}
            />
          </Box>
        ))}

        <Button startIcon={<AccountTreeIcon />} onClick={addGroup} variant="outlined" size="small" color="secondary">
          Add Nested Group
        </Button>
      </Stack>
    </Paper>
  );
};

const DeviceRulesComponent: React.FC<Props> = ({ deviceId }) => {
  const auth = useAuth();
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens]);
  
  const [rules, setRules] = useState<DeviceRule[]>([]);
  const [childDevices, setChildDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<DeviceRule | null>(null);

  // Form state - using full nested structure
  const [ruleName, setRuleName] = useState('');
  const [description, setDescription] = useState('');
  const [rootConditions, setRootConditions] = useState<RuleConditions>({
    operator: 'AND',
    conditions: [{ cameraId: '', comparison: '>=', threshold: 1, classes: [] }],
    groups: []
  });
  const [actionType, setActionType] = useState('relay');
  const [actionData, setActionData] = useState('{"type":"relay","relay_number":1,"action":"on","duration":5}');
  const [actionMode, setActionMode] = useState<'simple' | 'json'>('simple');
  const [targetRelayDeviceId, setTargetRelayDeviceId] = useState('');
  const [relayNumber, setRelayNumber] = useState(1);
  const [relayAction, setRelayAction] = useState<'on' | 'off'>('on');
  const [relayDuration, setRelayDuration] = useState(5);
  const [evaluationWindow, setEvaluationWindow] = useState(0);
  const [cooldown, setCooldown] = useState(60);
  const [isEnabled, setIsEnabled] = useState(true);
  const [sendSmsOnTrigger, setSendSmsOnTrigger] = useState(true);
  const [disableAfterTrigger, setDisableAfterTrigger] = useState(true);

  useEffect(() => {
    if (actionMode !== 'simple') return;
    const nextAction: RelayAction = {
      type: 'relay',
      relay_number: relayNumber,
      action: relayAction,
      duration: relayDuration,
      ...(targetRelayDeviceId ? { target_device_id: targetRelayDeviceId } : {}),
    };
    setActionData(JSON.stringify(nextAction));
  }, [actionMode, relayNumber, relayAction, relayDuration, targetRelayDeviceId]);

  useEffect(() => {
    loadRules();
    loadChildDevices();
  }, [deviceId]);

  const loadRules = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/devicerules/device/${deviceId}`, { headers: authHeader });
      if (res.ok) {
        const data = await res.json();
        setRules(data);
      } else {
        setError('Failed to load rules');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  const loadChildDevices = async () => {
    try {
      const res = await fetch(`/api/devices/${deviceId}/children`, { headers: authHeader });
      if (res.ok) {
        const data = await res.json();
        setChildDevices(data);
      }
    } catch (e: any) {
      console.error('Failed to load child devices:', e);
    }
  };

  const openAddDialog = () => {
    setEditingRule(null);
    resetForm();
    if (childDevices.length > 0) {
      setTargetRelayDeviceId(childDevices[0].id);
    }
    setDialogOpen(true);
  };

  const openEditDialog = (rule: DeviceRule) => {
    setEditingRule(rule);
    setRuleName(rule.ruleName);
    setDescription(rule.description || '');
    setEvaluationWindow(rule.evaluationWindowMinutes);
    setCooldown(rule.cooldownSeconds);
    setIsEnabled(rule.isEnabled);
    setSendSmsOnTrigger(rule.sendSmsOnTrigger ?? true);
    setDisableAfterTrigger(rule.disableAfterTrigger ?? true);

    // Parse conditions
    try {
      const cond = normalizeConditions(JSON.parse(rule.conditionsJson));
      setRootConditions(cond);
    } catch (e) {
      console.error('Failed to parse conditions:', e);
    }
    
    // Parse action
    if (rule.actionData) {
      try {
        setActionData(rule.actionData);
        const action = JSON.parse(rule.actionData);
        setActionType(action.type || 'relay');
        if (action.type === 'relay') {
          setTargetRelayDeviceId(action.target_device_id || action.targetDeviceId || '');
          setRelayNumber(Number(action.relay_number) || 1);
          setRelayAction(action.action === 'off' ? 'off' : 'on');
          setRelayDuration(Number(action.duration) || 5);
          setActionMode('simple');
        } else {
          setActionMode('json');
        }
      } catch (e) {
        console.error('Failed to parse action:', e);
        setActionMode('json');
      }
    } else {
      setActionMode('simple');
    }
    
    setDialogOpen(true);
  };

  const resetForm = () => {
    setRuleName('');
    setDescription('');
    setRootConditions({
      operator: 'AND',
      conditions: [{ cameraId: '', comparison: '>=', threshold: 1, classes: [] }],
      groups: []
    });
    setActionType('relay');
    setActionData('{"type":"relay","relay_number":1,"action":"on","duration":5}');
    setActionMode('simple');
    setTargetRelayDeviceId('');
    setRelayNumber(1);
    setRelayAction('on');
    setRelayDuration(5);
    setEvaluationWindow(0);
    setCooldown(60);
    setIsEnabled(true);
    setSendSmsOnTrigger(true);
    setDisableAfterTrigger(true);
  };

  const updateRootConditions = (newConditions: RuleConditions) => {
    setRootConditions(newConditions);
  };

  const cleanConditions = (cond: RuleConditions): RuleConditions => {
    const cleaned: RuleConditions = {
      operator: cond.operator,
      timeDelaySeconds: cond.timeDelaySeconds || undefined
    };
    
    if (cond.conditions && cond.conditions.length > 0) {
      cleaned.conditions = cond.conditions.filter(c => c.cameraId);
    }
    
    if (cond.groups && cond.groups.length > 0) {
      cleaned.groups = cond.groups.map(g => cleanConditions(g));
    }
    
    return cleaned;
  };

  const saveRule = async () => {
    setError(null);
    try {
      const conditionsObj = cleanConditions(rootConditions);

      let parsedAction: any = null;
      if (actionMode === 'simple') {
        parsedAction = {
          type: 'relay',
          relay_number: relayNumber,
          action: relayAction,
          duration: relayDuration,
          ...(targetRelayDeviceId ? { target_device_id: targetRelayDeviceId } : {}),
        };
      } else {
        try {
          parsedAction = JSON.parse(actionData);
        } catch (e) {
          setError('Invalid action JSON');
          return;
        }
      }

      const body = {
        parentDeviceId: deviceId,
        ruleName,
        conditions: conditionsObj,
        actionData: parsedAction,
        isEnabled,
        description: description || null,
        evaluationWindowMinutes: evaluationWindow,
        cooldownSeconds: cooldown,
        sendSmsOnTrigger,
        disableAfterTrigger,
      };

      const url = editingRule
        ? `/api/devicerules/${editingRule.id}`
        : `/api/devicerules`;
      
      const method = editingRule ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeader },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Failed to save rule');
      }

      await loadRules();
      setDialogOpen(false);
      resetForm();
    } catch (e: any) {
      setError(e.message || 'Failed to save rule');
    }
  };

  const deleteRule = async (ruleId: string) => {
    if (!confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const res = await fetch(`/api/devicerules/${ruleId}`, {
        method: 'DELETE',
        headers: authHeader,
      });

      if (res.ok) {
        await loadRules();
      } else {
        setError('Failed to delete rule');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to delete rule');
    }
  };

  const toggleRule = async (ruleId: string) => {
    try {
      const res = await fetch(`/api/devicerules/${ruleId}/toggle`, {
        method: 'POST',
        headers: authHeader,
      });

      if (res.ok) {
        await loadRules();
      } else {
        setError('Failed to toggle rule');
      }
    } catch (e: any) {
      setError(e.message || 'Failed to toggle rule');
    }
  };

  const parseConditions = (conditionsJson: string): string => {
    try {
      const cond = normalizeConditions(JSON.parse(conditionsJson));
      return formatConditions(cond, 0);
    } catch (e) {
      return 'Invalid conditions';
    }
  };

  const formatConditions = (cond: RuleConditions, depth: number): string => {
    const parts: string[] = [];
    
    // Format individual conditions
    cond.conditions?.forEach((c) => {
      const camera = childDevices.find(d => d.id === c.cameraId);
      const cameraName = camera?.name || c.cameraId.substring(0, 8);
      const classesStr = c.classes && c.classes.length > 0 ? ` (classes: ${c.classes.join(',')})` : '';
      parts.push(`${cameraName} ${c.comparison} ${c.threshold}${classesStr}`);
    });
    
    // Format nested groups
    cond.groups?.forEach((group) => {
      const delayStr = group.timeDelaySeconds ? ` after ${group.timeDelaySeconds}s` : '';
      parts.push(`(${formatConditions(group, depth + 1)}${delayStr})`);
    });
    
    return parts.join(` ${cond.operator} `);
  };

  const conditionPreview = useMemo(() => {
    const cleaned = cleanConditions(rootConditions);
    const summary = formatConditions(cleaned, 0).trim();
    return summary.length > 0 ? summary : 'No valid conditions yet. Select at least one camera condition.';
  }, [rootConditions, childDevices]);

  const isCrossCamera = useMemo(() => {
    const cameraIds = new Set<string>();
    const collectCameraIds = (cond: RuleConditions) => {
      cond.conditions?.forEach(c => { if (c.cameraId) cameraIds.add(c.cameraId); });
      cond.groups?.forEach(g => collectCameraIds(g));
    };
    collectCameraIds(rootConditions);
    return cameraIds.size > 1;
  }, [rootConditions]);

  if (loading) {
    return <Typography>Loading rules...</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Device Rules</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
          Add Rule
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {rules.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No rules configured. Create a rule to trigger actions based on detection conditions.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {rules.map((rule) => (
            <Card key={rule.id} variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="start" sx={{ mb: 1 }}>
                  <Box>
                    <Typography variant="h6">{rule.ruleName}</Typography>
                    {rule.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {rule.description}
                      </Typography>
                    )}
                  </Box>
                  <Chip 
                    label={rule.isEnabled ? 'Enabled' : 'Disabled'} 
                    color={rule.isEnabled ? 'success' : 'default'}
                    size="small"
                  />
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Conditions:</strong> {parseConditions(rule.conditionsJson)}
                </Typography>

                <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Cross-camera window: {rule.evaluationWindowMinutes > 0 ? `${rule.evaluationWindowMinutes}m` : '2m (default)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cooldown: {rule.cooldownSeconds}s
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SMS on trigger: {rule.sendSmsOnTrigger ? 'On' : 'Off'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Auto-disable after trigger: {rule.disableAfterTrigger ? 'On' : 'Off'}
                  </Typography>
                  {rule.lastTriggeredAt && (
                    <Typography variant="caption" color="text.secondary">
                      Last triggered: {new Date(rule.lastTriggeredAt).toLocaleString()}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
              
              <CardActions>
                <IconButton size="small" onClick={() => toggleRule(rule.id)} title={rule.isEnabled ? 'Disable' : 'Enable'}>
                  {rule.isEnabled ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <IconButton size="small" onClick={() => openEditDialog(rule)}>
                  <EditIcon />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => deleteRule(rule.id)}>
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </Stack>
      )}

      {/* Rule Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editingRule ? 'Edit Rule' : 'Create Rule'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Rule Name"
              value={ruleName}
              onChange={(e) => setRuleName(e.target.value)}
              fullWidth
              required
            />
            
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <Divider />
            <Typography variant="subtitle2">Conditions</Typography>

            <ConditionGroupBuilder
              conditions={rootConditions}
              onChange={updateRootConditions}
              childDevices={childDevices}
              depth={0}
            />

            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'background.default' }}>
              <Typography variant="caption" color="text.secondary">
                Condition Preview
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {conditionPreview}
              </Typography>
              {isCrossCamera && (
                <Typography variant="caption" color="info.main" sx={{ mt: 0.5, display: 'block' }}>
                  Cross-camera rule: when one camera triggers, the other camera's most recent detection within the cross-camera window will be checked.
                </Typography>
              )}
            </Paper>

            <Divider />
            <Typography variant="subtitle2">Action</Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Action Editor</InputLabel>
              <Select
                value={actionMode}
                onChange={(e) => {
                  const nextMode = e.target.value as 'simple' | 'json';
                  if (nextMode === 'simple') {
                    try {
                      const parsed = JSON.parse(actionData);
                      if (parsed.type === 'relay') {
                        setTargetRelayDeviceId(parsed.target_device_id || parsed.targetDeviceId || targetRelayDeviceId);
                        setRelayNumber(Number(parsed.relay_number) || relayNumber);
                        setRelayAction(parsed.action === 'off' ? 'off' : 'on');
                        setRelayDuration(Number(parsed.duration) || relayDuration);
                      }
                    } catch {
                      // Keep existing simple-form values if JSON is invalid
                    }
                  }
                  setActionMode(nextMode);
                }}
                label="Action Editor"
              >
                <MenuItem value="simple">Simple (recommended)</MenuItem>
                <MenuItem value="json">Advanced JSON</MenuItem>
              </Select>
            </FormControl>

            {actionMode === 'simple' ? (
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    label="Action Type"
                  >
                    <MenuItem value="relay">Relay</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Target Relay Device</InputLabel>
                  <Select
                    value={targetRelayDeviceId}
                    onChange={(e) => setTargetRelayDeviceId(e.target.value)}
                    label="Target Relay Device"
                  >
                    {childDevices.map((device) => (
                      <MenuItem key={device.id} value={device.id}>
                        {device.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Relay Number"
                    type="number"
                    value={relayNumber}
                    onChange={(e) => setRelayNumber(Math.max(1, parseInt(e.target.value) || 1))}
                    size="small"
                    sx={{ flex: 1 }}
                    helperText="Physical relay channel"
                  />

                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={relayAction}
                      onChange={(e) => setRelayAction(e.target.value as 'on' | 'off')}
                      label="Action"
                    >
                      <MenuItem value="on">Turn On</MenuItem>
                      <MenuItem value="off">Turn Off</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <TextField
                  label="On Duration (seconds)"
                  type="number"
                  value={relayDuration}
                  onChange={(e) => setRelayDuration(Math.max(1, parseInt(e.target.value) || 1))}
                  size="small"
                  fullWidth
                  disabled={relayAction === 'off'}
                  helperText="How long relay stays ON before auto-off"
                />

                <TextField
                  label="Generated Action JSON"
                  value={actionData}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{ readOnly: true }}
                  helperText="Auto-generated from the simple form"
                />
              </Stack>
            ) : (
              <TextField
                label="Action JSON"
                value={actionData}
                onChange={(e) => setActionData(e.target.value)}
                fullWidth
                multiline
                rows={3}
                helperText='Example: {"type":"relay","target_device_id":"<device-id>","relay_number":1,"action":"on","duration":5}'
              />
            )}

            <Divider />
            <Typography variant="subtitle2">Settings</Typography>

            <TextField
              label="Cross-Camera Window (minutes)"
              type="number"
              value={evaluationWindow}
              onChange={(e) => setEvaluationWindow(parseInt(e.target.value) || 0)}
              fullWidth
              helperText="How far back to check other cameras' detections for cross-camera rules (0 = 2 min default)"
            />

            <TextField
              label="Cooldown (seconds)"
              type="number"
              value={cooldown}
              onChange={(e) => setCooldown(parseInt(e.target.value))}
              fullWidth
              helperText="Minimum time between rule triggers"
            />

            <FormControlLabel
              control={<Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />}
              label="Enabled"
            />

            <FormControlLabel
              control={<Switch checked={sendSmsOnTrigger} onChange={(e) => setSendSmsOnTrigger(e.target.checked)} />}
              label="Send SMS when triggered"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              Texts the numbers configured in Device Settings (device.sms.number) with the rule name and the detection count/classes that triggered it.
            </Typography>

            <FormControlLabel
              control={<Switch checked={disableAfterTrigger} onChange={(e) => setDisableAfterTrigger(e.target.checked)} />}
              label="Disable rule after it triggers"
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              For one-shot actions like a trap relay — stops re-triggering until you manually re-enable the rule, and also stops the regular periodic detection SMS for this device until then.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveRule} variant="contained">
            {editingRule ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceRulesComponent;
